import { getLogmarLineSize500mm } from './functions';
import { calibrationConstants } from './calibration-constants';
import { useCalibration } from '../context/calibrationContext';
import { logmarData } from './data';
import { useCallback } from 'react';
import { useTestContext } from '../context/testContext';

export const useMaxLogmar = () => {
  const fontAdjustmentPercentage = 125.5;
  //this value is defined in testData but is actually a calibration constant. candidate for refactor.
  const { pixelDensityReference, patientDistanceReference } = calibrationConstants;
  const {
    updateTestData,
    testData: { testKey },
  } = useTestContext();
  const {
    calibrationData: { pixelDensity, distanceFromScreen, screenWidthPx, screenHeightPx },
  } = useCalibration();

  const getLogmarFontSize = useCallback(
    (logmar) => {
      const lineSize = getLogmarLineSize500mm(logmar);
      const fontSize = (lineSize * distanceFromScreen) / patientDistanceReference / pixelDensity;
      const fontSizeAdjustment = (pixelDensity * fontAdjustmentPercentage) / pixelDensityReference / 100;
      const adjustedFontSize = fontSize + fontSize * fontSizeAdjustment;
      return Math.round(adjustedFontSize);
    },
    [distanceFromScreen, patientDistanceReference, pixelDensity, pixelDensityReference],
  );

  const getSingleCharWidth = useCallback(
    (logmar) => {
      //font size defines height - calculate width
      const largestCharAspectRatio = 1.53;
      const fontSize = getLogmarFontSize(logmar);
      const largestSingleCharWidth = fontSize / largestCharAspectRatio;
      return largestSingleCharWidth;
    },
    [getLogmarFontSize],
  );

  const getMinWidthForText = useCallback(
    (logmar) => {
      const fontSize = getLogmarFontSize(logmar);
      const maxSingleCharWidth = getSingleCharWidth(logmar);
      const widthOfLongestString = maxSingleCharWidth * 5 + fontSize;
      //we add font size because the width of a space is fontSize / 4, and there will be 4 spaces in the largest string.
      const crowdedPaddingAndBorder = fontSize / 4 + fontSize / 1.5;
      const minWidthRequired = widthOfLongestString + crowdedPaddingAndBorder;
      return minWidthRequired;
    },
    [getLogmarFontSize, getSingleCharWidth],
  );

  const getMinWidthForOptotypes = useCallback(
    (logmar) => {
      const lineSize = getLogmarLineSize500mm(logmar);
      const optotypeSize = Math.round((lineSize * distanceFromScreen) / patientDistanceReference / pixelDensity);
      const widthOfMultipleOptotypes = optotypeSize * 5;
      const crowdedPaddingAndBorder = optotypeSize * 4.05;
      //border width is (size / 8) and there are 6 of them = size * 0.75
      //padding width is (size / 3) and there are 10 of them = size * 1.6
      //add these together = size * 2.35
      const minWidthRequired = widthOfMultipleOptotypes + crowdedPaddingAndBorder;
      return minWidthRequired;
    },
    [distanceFromScreen, patientDistanceReference, pixelDensity],
  );

  const getMinWidthRequired = useCallback(
    (logmar) => {
      if (testKey.startsWith('kays')) {
        const minWidthRequired = getMinWidthForOptotypes(logmar);
        return minWidthRequired;
      } else {
        const minWidthRequired = getMinWidthForText(logmar);
        return minWidthRequired;
      }
    },
    [testKey, getMinWidthForText, getMinWidthForOptotypes],
  );

  const updateMaxLogmar = useCallback(() => {
    const maxLogmar = logmarData.find((logmar) => {
      const minWidthRequired = getMinWidthRequired(logmar.id);
      return minWidthRequired < Math.max(screenWidthPx, screenHeightPx);
    });

    return updateTestData({ maxPermittedLogmar: maxLogmar });
  }, [screenWidthPx, screenHeightPx, getMinWidthRequired, updateTestData]);

  return updateMaxLogmar;
};
